import { AddOnPurpose } from '../../../generated/api/addOnPurpose';
import { OnlineModelCategory } from '../../../generated/api/onlineModelCategory';
import {
  type BaseRules,
  calculateDependency,
  getDefaultSelectedLanguage,
  isMobileVoiceAddOn,
} from '../../SubscriptionDetails/addOnDependencyUtils';
import type { BasketItem } from '../../../common/types/shoppingBasket';
import type { AddOnAssociation } from '../../../generated/api/addOnAssociation';
import type { ChildOrderItem } from '../../../generated/api/childOrderItem';
import type { CommercialProduct } from '../../../generated/api/commercialProduct';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

/**
 * Enforce group max constraints by removing BY_DEFAULT addons when user selects alternatives.
 * When multiple addons from the same group exceed the group max, we prefer user-selected
 * addons over BY_DEFAULT bundled addons.
 */
export const enforceGroupMaxConstraints = (
  addOnCodes: string[],
  commercialProductId: string,
  associations: AssociationRecord[],
  cpAddOnAssociations?: AddOnAssociation[]
): string[] => {
  // Get associations for this commercial product
  const cpAssociations = associations.filter(a => a.cspmb__price_item__c === commercialProductId);

  const groupInfo: Map<string, { max: number; addons: Array<{ code: string; isDefault: boolean }> }> = new Map();

  addOnCodes.forEach(code => {
    const assoc = cpAssociations.find(a => a.cspmb__add_on_price_item__r?.guid__c === code);
    if (assoc?.cspmb__group__c) {
      const groupName = assoc.cspmb__group__c;
      const max = assoc.cspmb__max__c ?? 100;

      // Determine if addon is bundled default:
      // 1. If addOnPurpose is defined, use it (BY_DEFAULT = true, OPTIONAL/MANDATORY = false)
      // 2. Only fallback to cspmb__default_quantity__c when addOnPurpose is undefined
      const cpAssoc = cpAddOnAssociations?.find(a => a.addOnCode === code);
      const isDefault =
        cpAssoc?.addOnPurpose !== undefined
          ? cpAssoc.addOnPurpose === AddOnPurpose.BY_DEFAULT
          : (assoc.cspmb__default_quantity__c ?? 0) > 0;

      if (!groupInfo.has(groupName)) {
        groupInfo.set(groupName, { max, addons: [] });
      }
      groupInfo.get(groupName)!.addons.push({ code, isDefault });
    }
  });

  // For each group with violations, remove BY_DEFAULT addons (or first addon as fallback)
  const codesToRemove = new Set<string>();

  groupInfo.forEach(info => {
    if (info.addons.length > info.max) {
      // Group max exceeded - remove BY_DEFAULT addons to make room for user selections
      const defaultAddons = info.addons.filter(a => a.isDefault);
      const nonDefaultAddons = info.addons.filter(a => !a.isDefault);

      if (defaultAddons.length > 0 && nonDefaultAddons.length > 0) {
        // Clear case: remove BY_DEFAULT addons, keep user-selected
        defaultAddons.forEach(a => codesToRemove.add(a.code));
      } else if (defaultAddons.length === 0 && info.addons.length > info.max) {
        // Fallback: cspmb__default_quantity__c not set, but group max exceeded
        // Keep the LAST addon(s) up to max (user selections are added after bundled defaults)
        const addonsToRemove = info.addons.slice(0, info.addons.length - info.max);
        addonsToRemove.forEach(a => codesToRemove.add(a.code));
      }
    }
  });

  return addOnCodes.filter(code => !codesToRemove.has(code));
};

/**
 * Validate add-ons using CloudSense dependency rules.
 * Removes incompatible addons, adds mandatory dependencies, and enforces group max constraints.
 */
export const getValidatedAddOns = (
  commercialProduct: CommercialProduct,
  selectedAddOnCodes: string[],
  associations: AssociationRecord[],
  dependencies: DependencyRecord[],
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
): ChildOrderItem[] => {
  const commercialProductId = commercialProduct.commercialProductId;
  const commercialProductCode = commercialProduct.commercialProductCode;

  if (!commercialProductId || !commercialProductCode || !associations.length) {
    // Fall back to simple mapping if no validation data available
    return selectedAddOnCodes.map(code => ({
      addOnCode: code,
      addOnAssociationCode:
        commercialProduct.addOnAssociations?.find(a => a.addOnCode === code)?.addOnAssociationCode ?? '',
    }));
  }

  // Build BaseRules for calculateDependency
  const baseRules: BaseRules = {
    commercialProductId,
    commercialProductCode,
    addOnAssociations: associations,
    addOnDependencies: dependencies,
  };

  // Get all available add-on codes for this commercial product
  const allAddOnCodes = associations
    .filter(a => a.cspmb__price_item__c === commercialProductId)
    .map(a => a.cspmb__add_on_price_item__r?.guid__c)
    .filter((code): code is string => code !== undefined);

  // Use existing calculateDependency utility
  const dependencyResult = calculateDependency(
    baseRules,
    selectedAddOnCodes,
    allAddOnCodes,
    false // don't flip any add-on
  );

  // Build final valid add-on codes:
  // 1. Start with selected codes
  // 2. Remove incompatible and dependent-to-delete codes
  // 3. Add mandatory dependent codes
  // NOTE: openingFeeCodesToDelete is for opening FEE handling, not addon removal
  const codesToRemove = new Set([
    ...dependencyResult.incompatibleAddOnCodes,
    ...dependencyResult.dependentCodesToDelete,
  ]);

  let validAddOnCodes = [
    ...selectedAddOnCodes.filter(code => !codesToRemove.has(code)),
    ...dependencyResult.dependentCodesToAdd,
  ];

  // Deduplicate
  validAddOnCodes = [...new Set(validAddOnCodes)];

  // Enforce group max constraints: remove BY_DEFAULT addons when user selects alternatives
  // This handles cases where CloudSense exclusion rules are not configured for the group
  validAddOnCodes = enforceGroupMaxConstraints(
    validAddOnCodes,
    commercialProductId,
    associations,
    commercialProduct.addOnAssociations
  );

  // Convert to ChildOrderItem format
  const validAddOns: ChildOrderItem[] = validAddOnCodes.map(addOnCode => {
    const association = associations.find(
      a => a.cspmb__price_item__c === commercialProductId && a.cspmb__add_on_price_item__r?.guid__c === addOnCode
    );

    const attributes = addOnAttributes?.[addOnCode];
    const defaultedAttributes =
      attributes || (isMobileVoiceAddOn(addOnCode) ? { language: getDefaultSelectedLanguage() } : undefined);
    return {
      addOnCode,
      addOnAssociationCode: association?.guid__c ?? '',
      ...(defaultedAttributes ? { addOnAttributes: defaultedAttributes } : {}),
    };
  });

  return validAddOns;
};

/**
 * Build validated add-ons for a basket item.
 * Applies CloudSense rules for SELF_SERVICE subscriptions.
 */
export const buildValidatedAddOnsForBasketItem = (
  basketItem: BasketItem,
  basketItemIndex: number,
  commercialProduct: CommercialProduct,
  onlineModel: OnlineModel,
  associations: AssociationRecord[],
  dependencies: DependencyRecord[]
): ChildOrderItem[] => {
  const addOns = basketItem.offer.addOns?.[basketItemIndex];
  const selectedAddOnCodes = addOns?.addOnCodes ?? [];
  const addOnAttributes = addOns?.addOnAttributes;

  if (onlineModel.category === OnlineModelCategory.SELF_SERVICE && associations.length > 0) {
    return getValidatedAddOns(commercialProduct, selectedAddOnCodes, associations, dependencies, addOnAttributes);
  }

  return selectedAddOnCodes.map(code => {
    const attributes = addOnAttributes?.[code];
    const defaultedAttributes =
      attributes || (isMobileVoiceAddOn(code) ? { language: getDefaultSelectedLanguage() } : undefined);
    return {
      addOnCode: code,
      addOnAssociationCode:
        commercialProduct.addOnAssociations?.find(a => a.addOnCode === code)?.addOnAssociationCode ?? '',
      ...(defaultedAttributes ? { addOnAttributes: defaultedAttributes } : {}),
    };
  });
};
